import { api } from './client';
import { FailureList, FailureFilter, FailureRecord, Failure } from '@/types/failure';
import { transformFailureToRecord, transformFiltersToFailureFilter } from '@/utils/dataTransform';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types';
import { PaginatedResponse } from '@/types/api';
import { filterOptionsService } from './filterOptions';

export interface FailuresApiResponse extends PaginatedResponse<FailureRecord> {
  failures: FailureRecord[];
}

/**
 * Real API service for failures data
 */
class FailuresApiService {
  /**
   * Get failures data with multi-select filtering and pagination
   */
  async getFailures(
    filters: FailureFilters = DEFAULT_FAILURE_FILTERS
  ): Promise<FailuresApiResponse> {
    try {
      const page = filters.page || 1;
      const size = filters.size || 20;

      // Check if any filters are applied
      const hasFilters =
        (filters.sites && filters.sites.length > 0) ||
        (filters.turbineModels && filters.turbineModels.length > 0) ||
        (filters.componentsMajor && filters.componentsMajor.length > 0) ||
        (filters.componentsMinor && filters.componentsMinor.length > 0) ||
        (filters.outcomeName && filters.outcomeName.length > 0) ||
        (filters.failureModes && filters.failureModes.length > 0) ||
        (filters.excludedCaseIds && filters.excludedCaseIds.length > 0) ||
        filters.operationTimeMin !== undefined ||
        filters.operationTimeMax !== undefined ||
        filters.dateFrom !== undefined ||
        filters.dateTo !== undefined;

      // Force use of analysis_filter for large requests (analysis purposes)
      const isAnalysisRequest = (filters.size || 20) >= 10000;

      let response: FailureList;

      if (!hasFilters && !isAnalysisRequest) {
        // No filters applied and not an analysis request - use GET /failures endpoint
        const urlWithParams = `/failures?size=${size}&page=${page}`;
        response = await api.get<FailureList>(urlWithParams);
      } else {
        // Filters applied OR analysis request - use POST filter endpoint
        const failureFilter: FailureFilter = await transformFiltersToFailureFilter(filters);

        // Always use analysis_filter for analysis requests to get censored data
        const endpoint = isAnalysisRequest
          ? '/failures/filter/analysis_filter'
          : '/failures/filter/failure_filter';

        const urlWithParams = `${endpoint}?size=${size}&page=${page}`;

        // Send only the filter parameters in the body (no page/size)
        response = await api.post<FailureList>(urlWithParams, failureFilter);
      }

      const transformedFailures = response.failures.map(transformFailureToRecord);

      return {
        total_count: response.total_count,
        total_pages: response.total_pages,
        page: response.page,
        size: response.size,
        has_more: response.has_more,
        failures: transformedFailures,
      };
    } catch (error) {
      console.error('Failed to fetch failures:', error);
      return {
        total_count: 0,
        total_pages: 0,
        page: filters.page || 1,
        size: filters.size || 20,
        has_more: false,
        failures: [],
      };
    }
  }

  /**
   * Legacy method for backward compatibility with single-select filters
   */
  async getFailuresLegacy(filters: Record<string, unknown> = {}): Promise<FailuresApiResponse> {
    const failureFilters: FailureFilters = {
      sites: filters.site_name ? [filters.site_name as string] : [],
      turbineModels: filters.turbine_model ? [filters.turbine_model as string] : [],
      componentsMajor: filters.component_major ? [filters.component_major as string] : [],
      componentsMinor: filters.component_minor ? [filters.component_minor as string] : [],
      outcomeName: filters.outcome_name ? [filters.outcome_name as string] : [],
      failureModes: filters.failure_mode ? [filters.failure_mode as string] : [],
      dateFrom: filters.date_from as string,
      dateTo: filters.date_to as string,
      operationTimeMin: filters.operation_time_min as number,
      operationTimeMax: filters.operation_time_max as number,
      page: filters.page as number,
      size: filters.size as number,
    };

    return this.getFailures(failureFilters);
  }

  /**
   * Get a specific failure record by ID
   */
  async getFailureById(id: string): Promise<FailureRecord | null> {
    try {
      const response = await api.get<{ failure: Failure }>(`/failures/${id}`);
      return transformFailureToRecord(response.failure);
    } catch (error) {
      console.error('Failed to fetch failure by ID:', error);
      return null;
    }
  }

  /**
   * Get failures for specific turbines (for Weibull analysis)
   */
  async getFailuresForTurbines(turbineIds: string[]): Promise<FailureRecord[]> {
    try {
      const response = await this.getFailures({ size: 100000, page: 1 });
      return response.failures.filter((f) => turbineIds.includes(f.turbine_name));
    } catch (error) {
      console.error('Failed to fetch failures for turbines:', error);
      return [];
    }
  }

  /**
   * Get failures for Weibull analysis with operation time focus
   */
  async getFailuresForWeibullAnalysis(filters: FailureFilters = DEFAULT_FAILURE_FILTERS): Promise<{
    failures: FailureRecord[];
    operationTimes: number[];
    summary: {
      count: number;
      avgOperationTime: number;
      minOperationTime: number;
      maxOperationTime: number;
      components: string[];
      outcomeName: string[];
    };
  }> {
    const response = await this.getFailures({ ...filters, size: 100000, page: 1 });
    const operationTimes = response.failures.map((f) => f.operation_time_years);

    return {
      failures: response.failures,
      operationTimes,
      summary: {
        count: response.failures.length,
        avgOperationTime:
          operationTimes.length > 0
            ? operationTimes.reduce((sum, time) => sum + time, 0) / operationTimes.length
            : 0,
        minOperationTime: operationTimes.length > 0 ? Math.min(...operationTimes) : 0,
        maxOperationTime: operationTimes.length > 0 ? Math.max(...operationTimes) : 0,
        components: Array.from(new Set(response.failures.map((f) => f.component_major))),
        outcomeName: Array.from(new Set(response.failures.map((f) => f.outcome_name))),
      },
    };
  }

  /**
   * Get unique filter options for dropdowns - uses the unified endpoint
   */
  async getFilterOptions(): Promise<{
    sites: string[];
    turbineModels: string[];
    componentsMajor: string[];
    componentsMinor: string[];
    failureModes: string[];
    outcomeName: string[];
    excludedCaseIds: string[];
  }> {
    try {
      const options = await filterOptionsService.getFilterOptions();
      return {
        ...options,
        excludedCaseIds: [], // Always empty initially
      };
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
      return {
        sites: [],
        turbineModels: [],
        componentsMajor: [],
        componentsMinor: [],
        failureModes: [],
        outcomeName: [],
        excludedCaseIds: [],
      };
    }
  }

  /**
   * Export filtered failures data
   */
  async exportFailures(
    filters: FailureFilters = DEFAULT_FAILURE_FILTERS,
    format: 'csv' | 'json' = 'csv'
  ): Promise<string> {
    try {
      const response = await this.getFailures({ ...filters, size: 100000 });

      if (format === 'json') {
        return JSON.stringify(response.failures, null, 2);
      }

      const headers = [
        'Failure ID',
        'Turbine Name',
        'Site Name',
        'Turbine Model',
        'Component Major',
        'Component Minor',
        'Failure Mode',
        'Failure Type',
        'Failure Date',
        'Restoration Date',
        'Detected Date',
        'Commissioning Date',
        'Operation Time (Years)',
        'Case ID',
      ];

      const csvRows = [
        headers.join(','),
        ...response.failures.map((f) =>
          [
            f.failure_id,
            f.turbine_name,
            f.site_name,
            f.turbine_model,
            f.component_major,
            f.component_minor,
            f.failure_mode,
            f.outcome_name,
            f.failure_date,
            f.restoration_date,
            f.detected_date,
            f.commissioning_date,
            f.operation_time_years,
            f.case_id,
          ]
            .map((field) => `"${field}"`)
            .join(',')
        ),
      ];

      return csvRows.join('\n');
    } catch (error) {
      console.error('Failed to export failures:', error);
      return '';
    }
  }

  /**
   * Get breakdown and statistics for analysis
   */
  async getFilterStatistics(filters: FailureFilters = DEFAULT_FAILURE_FILTERS): Promise<{
    totalRecords: number;
    siteBreakdown: { [site: string]: number };
    componentBreakdown: { [component: string]: number };
    failureTypeBreakdown: { [type: string]: number };
    operationTimeStats: {
      min: number;
      max: number;
      avg: number;
      median: number;
    };
  }> {
    try {
      const response = await this.getFailures({ ...filters, size: 100000 });
      const failures = response.failures;

      const siteBreakdown: Record<string, number> = {};
      const componentBreakdown: Record<string, number> = {};
      const failureTypeBreakdown: Record<string, number> = {};

      failures.forEach((f) => {
        siteBreakdown[f.site_name] = (siteBreakdown[f.site_name] || 0) + 1;
        componentBreakdown[f.component_major] = (componentBreakdown[f.component_major] || 0) + 1;
        failureTypeBreakdown[f.outcome_name] = (failureTypeBreakdown[f.outcome_name] || 0) + 1;
      });

      const operationTimes = failures.map((f) => f.operation_time_years).sort((a, b) => a - b);
      const median =
        operationTimes.length > 0 ? operationTimes[Math.floor(operationTimes.length / 2)] : 0;

      return {
        totalRecords: failures.length,
        siteBreakdown,
        componentBreakdown,
        failureTypeBreakdown,
        operationTimeStats: {
          min: operationTimes[0] || 0,
          max: operationTimes[operationTimes.length - 1] || 0,
          avg:
            operationTimes.length > 0
              ? operationTimes.reduce((sum, t) => sum + t, 0) / operationTimes.length
              : 0,
          median,
        },
      };
    } catch (error) {
      console.error('Failed to get filter statistics:', error);
      return {
        totalRecords: 0,
        siteBreakdown: {},
        componentBreakdown: {},
        failureTypeBreakdown: {},
        operationTimeStats: { min: 0, max: 0, avg: 0, median: 0 },
      };
    }
  }
}

// Export singleton
export const failuresApi = new FailuresApiService();
export type { FailureRecord };
